/**
 * Date helpers. A "YYYY-MM-DD" string is a calendar day and is always parsed
 * as LOCAL time — never `new Date("2026-03-01")`, which is UTC midnight and
 * shows as the previous evening in the US.
 */

import {
  addDays,
  eachDayOfInterval,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  format,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subMonths,
  subQuarters,
  subWeeks,
  subYears,
} from 'date-fns';

export function todayLocalISO(): string {
  return format(new Date(), 'yyyy-MM-dd');
}

export function parseISODate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const [y, m, d] = value.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

export function toISODate(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

type Style = 'numeric' | 'short' | 'long' | 'weekdayShort' | 'weekdayLong' | 'monthYear' | 'monthDay';

const STYLES: Record<Style, Intl.DateTimeFormatOptions> = {
  numeric: { month: 'numeric', day: 'numeric', year: 'numeric' },
  short: { month: 'short', day: 'numeric', year: 'numeric' },
  long: { month: 'long', day: 'numeric', year: 'numeric' },
  weekdayShort: { weekday: 'short', month: 'short', day: 'numeric' },
  weekdayLong: { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' },
  monthYear: { month: 'short', year: 'numeric' },
  monthDay: { month: 'short', day: 'numeric' },
};

/** Formats a date-only string without a timezone shift. Timestamps are formatted as instants. */
export function formatDate(value: string | null | undefined, style: Style = 'numeric'): string {
  if (!value) return '';
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value) ? parseISODate(value) : new Date(value);
  if (!date || Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', STYLES[style]);
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** "HH:MM" → "7:30 PM" */
export function formatTime(value: string | null | undefined): string {
  if (!value) return '';
  const [h, m] = value.split(':').map(Number);
  if (h == null || Number.isNaN(h)) return value;
  const period = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${String(m ?? 0).padStart(2, '0')} ${period}`;
}

export function addDaysISO(value: string, days: number): string {
  const date = parseISODate(value);
  return date ? toISODate(addDays(date, days)) : value;
}

/** Every day from start to end inclusive, in local time (no UTC drift across DST). */
export function eachDayISO(start: string, end: string | null | undefined): string[] {
  const s = parseISODate(start);
  const e = parseISODate(end ?? start) ?? s;
  if (!s || !e || e < s) return s ? [toISODate(s)] : [];
  return eachDayOfInterval({ start: s, end: e }).map(toISODate);
}

export type PresetKey =
  | 'today'
  | 'yesterday'
  | 'thisWeek'
  | 'thisMonth'
  | 'thisQuarter'
  | 'thisYear'
  | 'lastWeek'
  | 'lastMonth'
  | 'lastQuarter'
  | 'lastYear';

export function presetRange(key: PresetKey): { start: string; end: string } {
  const now = new Date();
  const r = (a: Date, b: Date) => ({ start: toISODate(a), end: toISODate(b) });
  switch (key) {
    case 'today':
      return r(now, now);
    case 'yesterday': {
      const y = addDays(now, -1);
      return r(y, y);
    }
    case 'thisWeek':
      return r(startOfWeek(now), endOfWeek(now));
    case 'thisMonth':
      return r(startOfMonth(now), endOfMonth(now));
    case 'thisQuarter':
      return r(startOfQuarter(now), endOfQuarter(now));
    case 'thisYear':
      return r(startOfYear(now), endOfYear(now));
    case 'lastWeek': {
      const s = startOfWeek(subWeeks(now, 1));
      return r(s, endOfWeek(s));
    }
    case 'lastMonth': {
      const s = startOfMonth(subMonths(now, 1));
      return r(s, endOfMonth(s));
    }
    case 'lastQuarter': {
      const s = startOfQuarter(subQuarters(now, 1));
      return r(s, endOfQuarter(s));
    }
    case 'lastYear': {
      const s = startOfYear(subYears(now, 1));
      return r(s, endOfYear(s));
    }
  }
}
